<template>
  <div>
    <b-card
      v-if="isLoading"
      class="p-3"
    >
      <div class="d-flex justify-content-center">
        <b-spinner label="Chargement..." />
      </div>
    </b-card>
    <b-card
      v-else
      title="Modifier le dépôt logistique"
      aria-label="Formulaire de modification de dépôt logistique"
      role="form"
    >
      <b-form @submit.prevent="onSubmit">
        <!-- Information section -->
        <h5
          id="general-info-title"
          class="mb-2"
        >
          Informations générales du dépôt
        </h5>
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Nom du dépôt *"
              label-for="name"
              aria-label="Nom du dépôt (obligatoire)"
              description="Nom officiel du dépôt logistique. Champ obligatoire."
            >
              <b-form-input
                id="name"
                v-model="form.name"
                :state="nameState"
                placeholder="Ex : Dépôt Central Tunis"
                aria-required="true"
                aria-describedby="name-desc"
              />
              <b-form-invalid-feedback id="name-desc">
                Le nom du dépôt est requis pour l'identification logistique.
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Gouvernorats *"
              label-for="governorate_ids"
              aria-label="Gouvernorats (obligatoire)"
              description="Sélectionnez les gouvernorats couverts par ce dépôt. Au moins un gouvernorat est requis."
            >
              <v-select
                id="governorate_ids"
                v-model="form.governorate_ids"
                :options="governorates"
                :reduce="governorate => governorate.id"
                label="name"
                multiple
                placeholder="Sélectionner les gouvernorats"
                :state="governorateState"
                aria-required="true"
                aria-describedby="governorate-desc"
              />
              <b-form-invalid-feedback id="governorate-desc">
                Au moins un gouvernorat est requis pour localiser le dépôt.
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Téléphone du dépôt"
              label-for="phone"
              aria-label="Numéro de téléphone du dépôt"
              description="Numéro de contact du responsable du dépôt (8 chiffres)."
            >
              <b-form-input
                id="phone"
                v-model="form.phone"
                :state="phoneState"
                placeholder="Ex : 71234567"
                maxlength="8"
                aria-describedby="phone-desc"
              />
              <b-form-invalid-feedback
                v-if="form.phone && !phoneState"
                id="phone-desc"
              >
                Le numéro de téléphone doit contenir au maximum 8 chiffres (format tunisien).
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Adresse email du dépôt"
              label-for="email"
              aria-label="Adresse email du dépôt"
              description="Adresse email professionnelle pour la gestion logistique."
            >
              <b-form-input
                id="email"
                v-model="form.email"
                type="email"
                :state="emailState"
                placeholder="Ex : depot@entreprise.com"
                aria-describedby="email-desc"
              />
              <b-form-invalid-feedback
                v-if="form.email"
                id="email-desc"
              >
                Veuillez entrer une adresse email valide (exemple : depot@entreprise.com)
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Adresse complète du dépôt"
              label-for="address"
              aria-label="Adresse complète du dépôt"
              description="Adresse physique détaillée pour la localisation du dépôt."
            >
              <b-form-textarea
                id="address"
                v-model="form.address"
                placeholder="Ex : 12 Rue de la Logistique, ZI Charguia, Tunis"
                rows="3"
                class="address-textarea"
                aria-describedby="address-desc"
              />
              <small
                id="address-desc"
                class="form-text text-muted"
              >Indiquez l'adresse exacte pour faciliter la livraison et la collecte.</small>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Details section -->
        <h5
          id="details-title"
          class="mt-3 mb-2"
        >
          Détails logistiques supplémentaires
        </h5>
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Espace disponible (m²)"
              label-for="available_space"
              aria-label="Espace disponible en mètres carrés"
              description="Surface logistique exploitable pour le stockage."
            >
              <b-form-input
                id="available_space"
                v-model="form.available_space"
                type="number"
                placeholder="Ex : 500"
                min="0"
                aria-describedby="space-desc"
              />
              <small
                id="space-desc"
                class="form-text text-muted"
              >Indiquez la surface totale disponible pour le stockage (en m²).</small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <template #label>
                <div class="d-flex align-items-center">
                  <span class="mr-1">Dépôt en location</span>
                </div>
              </template>
              <b-form-checkbox
                v-model="form.is_under_rent"
                name="is_under_rent"
                switch
                aria-label="Le dépôt est-il en location ?"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row v-if="form.is_under_rent">
          <b-col md="6">
            <b-form-group
              label="Montant du loyer mensuel (TND)"
              label-for="monthly_rent_amount"
              aria-label="Montant du loyer mensuel en dinars tunisiens"
              description="Indiquez le montant du loyer mensuel si le dépôt est en location."
            >
              <b-form-input
                id="monthly_rent_amount"
                v-model="form.monthly_rent_amount"
                type="number"
                placeholder="Ex : 1200"
                min="0"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group>
              <template #label>
                <div class="d-flex align-items-center">
                  <span class="mr-1">Dépôt actif</span>
                </div>
              </template>
              <b-form-checkbox
                v-model="form.is_active"
                name="is_active"
                switch
                aria-label="Le dépôt est-il actif ?"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Submit buttons -->
        <div class="d-flex justify-content-between mt-3">
          <b-button
            type="button"
            variant="outline-secondary"
            :to="{ name: 'repository-list' }"
            aria-label="Annuler la modification du dépôt"
          >
            Annuler
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="isSubmitting"
            aria-label="Enregistrer les modifications du dépôt logistique"
          >
            <b-spinner
              v-if="isSubmitting"
              small
              class="mr-1"
            />
            <span>{{ isSubmitting ? 'Modification en cours...' : 'Enregistrer les modifications du dépôt logistique' }}</span>
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BCard,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BSpinner,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  components: {
    BCard,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    vSelect,
    BButton,
    BSpinner,
    BFormInvalidFeedback,
  },
  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      repository: {},
      governorates: [],
      form: {
        name: '',
        phone: '',
        email: '',
        address: '',
        governorate_ids: [],
        is_active: true,
        is_under_rent: false,
        available_space: null,
        monthly_rent_amount: null,
      },
    }
  },

  computed: {
    nameState() {
      return this.form.name ? true : null
    },
    emailState() {
      if (!this.form.email) return null
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(this.form.email)
    },
    phoneState() {
      if (!this.form.phone) return null
      return this.form.phone.replace(/\D/g, '').length <= 8
    },
    governorateState() {
      return this.form.governorate_ids && this.form.governorate_ids.length > 0 ? true : null
    },
  },

  created() {
    this.fetchRepository()
    this.fetchGovernorates()
  },

  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    async fetchRepository() {
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/repositories/detail/${id}/`)
        this.repository = data
        this.populateForm(data)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repository:', error)
        this.showToast('danger', 'Erreur lors du chargement des données du dépôt')
      } finally {
        this.isLoading = false
      }
    },

    async fetchGovernorates() {
      try {
        const { data } = await axios.get('/api/governorates/')
        this.governorates = data.results || data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching governorates:', error)
        this.showToast('danger', 'Erreur lors du chargement des gouvernorats')
      }
    },

    populateForm(data) {
      this.form = {
        name: data.name || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        governorate_ids: data.governorates ? data.governorates.map(g => g.id) : [],
        is_active: data.is_active !== undefined ? data.is_active : true,
        is_under_rent: data.is_under_rent !== undefined ? data.is_under_rent : false,
        available_space: data.available_space || null,
        monthly_rent_amount: data.monthly_rent_amount || null,
      }
    },

    async onSubmit() {
      // Form validation
      if (!this.form.name || this.form.name.trim() === '') {
        this.showToast('danger', 'Le nom du dépôt est requis')
        return
      }

      if (!this.form.governorate_ids || this.form.governorate_ids.length === 0) {
        this.showToast('danger', 'Au moins un gouvernorat est requis')
        return
      }

      if (this.form.email && !this.emailState) {
        this.showToast('danger', 'Veuillez entrer une adresse email valide')
        return
      }

      if (this.form.phone && !this.phoneState) {
        this.showToast('danger', 'Le numéro de téléphone doit contenir au maximum 8 chiffres')
        return
      }

      // Clean phone number before submission - remove all non-digit characters
      if (this.form.phone) {
        this.form.phone = this.form.phone.replace(/\D/g, '')
      }

      this.isSubmitting = true

      try {
        const { id } = this.$route.params
        await axios.put(`/api/repositories/update/${id}/`, this.form)

        this.showToast('success', 'Dépôt mis à jour avec succès')
        this.$router.push({ name: 'repository-list' })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating repository:', error)
        this.showToast('danger', 'Erreur lors de la mise à jour du dépôt')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.form-group {
  margin-bottom: 1.5rem;
}

.form-control:focus, .custom-select:focus {
  border-color: #7367f0;
  box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.2);
}

.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #7367f0;
  border-color: #7367f0;
}

.address-textarea {
  resize: vertical;
  min-height: 70px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.2);
  }
}
</style>
